<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <div :class="$style.homeMenuPage">
    <div v-if="isAuthenticated && !isGuest && user"
         :class="$style.homePastOrder">
      <button :class="$style.pastOrderBtn"
              @click.stop="togglePastOrders(true)">
        <span :class="$style.pastOrderImage">
          <svg v-svg
               :class="$style.pastOrderIcon"
               symbol="past-order"/>
        </span>
        Past Order
      </button>
      <div :class="$style.homePastTitle">
        <h5>Look at past orders</h5>
        <svg v-svg
             :class="$style.curvedArrowUp"
             symbol="curved-arrow"/>
      </div>
    </div>

    <div v-else
         :class="$style.claimAccountSection">
      <div :class="$style.homeMenuContainer">
        <div :class="$style.claimAccount">
          <div :class="$style.claimAccountLogo">
            <img src="@/assets/img/white.png"
                 alt="Burger Boss">
          </div>
          <div :class="$style.claimAccountText">
            <div :class="$style.claimAccountTitle">
              <h2>Saved orders at one of our Kiosks? </h2>
              <span :class="$style.newLabel">New</span>
            </div>
            <div :class="$style.claimAccountInfo">
              <h6>Look up your last 5 orders!</h6>
              <span/>
            </div>
          </div>
          <BaseButton :class="$style.claimAccountBtn"
                      button-style="Main"
                      @click.stop="claimAccount">
            Claim Account
          </BaseButton>
        </div>

        <div :class="$style.homePastTitle">
          <h5>Look at past orders</h5>
          <svg v-svg
               :class="$style.curvedArrowUp"
               symbol="curved-arrow"/>
        </div>

      </div>
    </div>

    <div :class="$style.homeMenuCenter">
      <Logo :class="$style.homeMenuLogo"/>
    </div>

    <div :class="$style.homeMenuMain">
      <div :class="$style.homeMenuContainer">
        <div :class="$style.homeStartTitle">
          <h5>Start a new order</h5>
          <svg v-svg
               :class="$style.curvedArrowDown"
               symbol="curved-arrow"/>
        </div>

        <h2 :class="$style.homeMenuTitle"><span>Our Menu</span></h2>

        <div :class="$style.homeMenuButtons">
          <BaseButton v-show="!isGuest"
                      :class="$style.pastOrdersMobile"
                      button-style="Alpha"
                      @click.stop="togglePastOrders(true)">
            <h6>Look up my past orders</h6>
            <p>Up to last 5 orders</p>
          </BaseButton>
          <template v-for="product in sortedProducts">
            <BaseButton :key="product.key"
                        :class="$style.homeMenuBtn"
                        button-style="Alpha"
                        @click="openConfigurator(product.key)">
              {{ product.name }}
            </BaseButton>
          </template>
        </div>

      </div>
    </div>

    <PastOrders v-if="pastOrdersDrawer || kioskPastOrdersOpen"
                :value="pastOrdersDrawer || kioskPastOrdersOpen"
                @closeDrawer="togglePastOrders(false)"/>

    <LoadingSpinner v-if="menuIsLoading"
                    fixed
    />
  </div>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import Logo from '@/components/Logo';
import BaseButton from '@/components/BaseButton';
import PastOrders from '@/apps/cart/components/PastOrders';
import { CONFIGURATOR_PRODUCT } from '@/apps/configurator/store/types';
import { Action, Getter, Mutation } from 'vuex-class';
import { AUTH_LOGOUT, AUTH_MODAL } from '@/apps/auth/store/types';
import { FriesMenu, KidsMenu, LazyMenuItems } from '@/apps/menu/constants';
import { SET_PAST_ORDERS_SHOW } from '@/apps/kiosk/store/types';
import { menuItemsSorting } from '@/lib/helpers';
import LoadingSpinner from '@/apps/configurator/components/LoadingSpinner';
import LoadMenuMixin from '@/lib/mixins/load-menu-mixin';

/**
 * Page for desktop menu.
 */
@Component({
  components: {
    LoadingSpinner,
    Logo,
    PastOrders,
    BaseButton,
  },
  mixins: [
    LoadMenuMixin,
  ],
})
export default class DesktopMenu extends Vue {
  /**
   * Past order drawer visibility.
   * @type {Boolean}
   */
  pastOrdersDrawer = false;

  /**
   * Retrieves products from store.
   */
  @Getter products;
  @Getter pastOrdersShow;
  @Getter isKioskPlatform;
  @Getter authPhone;

  @Action(SET_PAST_ORDERS_SHOW) setPastOrdersShow;
  /**
   * Sets product key to be configured.
   */
  @Action(CONFIGURATOR_PRODUCT) setCurrentProduct;

  /**
   * Toggle past orders modal.
   * @param {Boolean} value.
   */
  togglePastOrders(value) {
    if (this.isKioskPlatform) {
      if (!this.authPhone) {
        this.$router.push({ name: 'number-lookup' });

        return;
      }
      this.setPastOrdersShow({ show: value });
    }

    this.pastOrdersDrawer = value;
  }

  /**
   * Should open page with past orders for kiosk if user select Past Orders button in main.
   */
  get kioskPastOrdersOpen() {
    return this.isKioskPlatform && this.pastOrdersShow;
  }

  /**
   * Set product for configurator and redirect user to configurator page
   * @param productKey - key to set product from configurator.
   */
  async openConfigurator(productKey) {
    if (Object.keys(LazyMenuItems).includes(productKey)) {
      this.$router.push({
        name:   'lazy-menu',
        params: {
          baseProductKey: LazyMenuItems[productKey].productKey,
          lazyProductKey: productKey,
        },
      });

      return;
    }

    switch (productKey) {
      case KidsMenu.PRODUCT_ID:
        this.$router.push({ name: 'kids-menu' });
        break;

      case FriesMenu.PRODUCT_ID:
        this.$router.push({ name: 'fries-menu' });
        break;

      default:
        await this.setCurrentProduct({ productKey });
        this.$router.push({
          name:   'configurator',
          params: { id: productKey },
        });
    }
  }

  @Getter isAuthenticated;
  @Getter('authUser') user;
  @Getter isGuest;
  @Mutation(AUTH_MODAL) showModal;
  @Action(AUTH_LOGOUT) authLogout;

  /**
   * Mutate state to open signIn modal
   */
  claimAccount() {
    this.showModal({
      name:  'claimAccount',
      value: true,
    });
  }

  /* Get sorted products*/
  get sortedProducts() {
    return menuItemsSorting({
      ...this.products,
      ...LazyMenuItems,
    });
  }
}
</script>

<style lang="scss" module>
@import "~@/style/variables.scss";
@import "~@/style/helpers.scss";

$claimAccountLogoWidth: 95px;
$claimAccountBtnWidth: 300px;

.homeMenuPage {
  min-height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.homeMenuContainer {
  max-width: 1240px;
  margin: 0 auto;
}

.claimAccountSection .homeMenuContainer {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.claimAccount {
  width: 100%;
  background-color: $white;
  padding: 14px 37px 10px 34px;
  margin-top: 32px;
  display: flex;
  align-items: center;

  .claimAccountLogo {
    width: $claimAccountLogoWidth;
  }

  .claimAccountText {
    width: calc(100% -
    #{$claimAccountLogoWidth + $claimAccountBtnWidth}
    );
    padding-left: 38px;
    padding-right: 22px;
  }

  .claimAccountBtn {
    width: $claimAccountBtnWidth;
    height: 60px;
  }
}

.claimAccountTitle {
  display: flex;
  align-items: center;
  font-family: 'Gotham', sans-serif;
}

.claimAccountInfo {
  display: flex;
  align-items: center;

  h6 {
    font-family: "LP Bambus", sans-serif;
    font-size: 20px;
    line-height: 28px;
    font-weight: 400;
    color: $black;
    margin-right: 29px;
  }

  span {
    background-image: url("/static/pattern.png");
    background-repeat: repeat-x;
    background-position: center;
    height: 8px;
    flex-grow: 1;
  }
}

.homeMenuCenter {
  text-align: center;
  margin-top: -42px;
}

.claimAccountSection + .homeMenuCenter {
  margin-top: 0;
}

.homeMenuLogo {
  margin: 0 auto;
}

.homeStartTitle {
  align-self: flex-end;
  position: relative;
  display: inline-block;
  margin-bottom: 55px;

  h5 {
    color: $white;
    font-family: 'LP Bambus', sans-serif;
    font-weight: 400;
    font-size: 27px;
    letter-spacing: 0.75px;
    line-height: 22px;
  }

  .curvedArrowDown {
    width: 34px;
    height: 45px;
    fill: $white;
    transform: rotateX(180deg);
    position: absolute;
    left: calc(100% + 5px);
    top: calc(100% - 10px);
  }
}

.homePastTitle {
  position: relative;
  margin-top: 26px;
  margin-right: 80px;

  h5 {
    color: $white;
    font-family: 'LP Bambus', sans-serif;
    font-weight: 400;
    font-size: 27px;
    letter-spacing: 0.75px;
    line-height: 22px;

  }

  .curvedArrowUp {
    width: 34px;
    height: 45px;
    fill: $white;
    position: absolute;
    left: calc(100% + 10px);
    top: -27px;
  }
}

.newLabel {
  display: inline-block;
  background-color: $green-color;
  color: $white;
  font-size: 11px;
  font-weight: 700;
  border-radius: 9.7px;
  text-transform: uppercase;
  padding: 1px 5px;
  height: 19.5px;
  line-height: 20px;
  margin-left: 10px;
}

.homeMenuMain {
  padding-bottom: 47px;
  margin-top: 30px;

  .homeMenuButtons {
    padding: 0 72px;
    display: flex;
    flex-flow: row wrap;
  }
}

.homeMenuTitle {
  @extend %main-title;
  font-size: 48px;
  line-height: 44px;
  text-transform: uppercase;
  margin-bottom: 79px;
  display: none;

  span::after,
  span::before {
    border-top: 7px solid #38AA3F;
    top: calc(50% - 5.5px);
    width: 120px;
  }

  span::before {
    left: -178px;
  }

  span::after {
    right: -178px;
  }
}

.homePastOrder {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-top: 25px;
  padding-right: 33px;
}

.claimAccountSection .homePastTitle {
  margin-top: 50px;
  margin-right: 130px;
}

.pastOrderBtn {
  display: flex;
  align-items: center;
  font-family: 'Gotham', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: $white-slightly-transparent;
  text-transform: uppercase;
  outline: none;

  .pastOrderImage {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 2px solid $green-color;
    margin-right: 12px;
  }
}

.pastOrderIcon {
  width: 48px;
  height: 48px;
}

.homePastOrder + .homeMenuCenter .homePastTitle {
  margin-right: 0;
}

.homeMenuButtons .pastOrdersMobile {
  text-align: center;
  background-image: url("/static/white.png");
  background-position: 42px center;
  margin-bottom: 61px;
  display: none;

  div {
    flex-direction: column;
  }

  h6 {
    font-size: 34px;
    line-height: 38px;
    margin-bottom: 4px;
  }

  p {
    font-size: 26px;
    font-weight: 300;
    line-height: 31px;
    margin-bottom: 0;
    text-transform: none;
  }
}


@media screen and (max-width: $kiosk-breakpoint-max) {
  .homeMenuPage {
    background-size: 0;
    background-color: $white;
  }
  .claimAccountSection,
  .homeStartTitle,
  .homePastOrder {
    display: none;
  }
  .homeMenuMain .homeMenuButtons {
    width: 925px;
    padding: 0;
    margin: 0 auto;
  }
  .homeMenuTitle,
  .homeMenuButtons .pastOrdersMobile {
    display: block;
  }
  .homeMenuMain {
    margin-top: 80px;
  }
  .homeMenuCenter,
  .claimAccountSection + .homeMenuCenter {
    margin-top: 0;
    padding-top: 20px;
  }
}

@media screen and (max-width: 965px) {
  .homeMenuMain .homeMenuButtons {
    width: calc(100% - 40px); /* 100% - width if margins */
    margin: 0 20px;
  }
  .homeMenuMain {
    margin-top: 20px;
    padding-bottom: 10px;
  }
  .homeMenuLogo {
    max-width: 169px;
  }
  .homeMenuTitle {
    display: none;
  }
  .homeMenuButtons .pastOrdersMobile {
    padding: 17px;
    flex-direction: column;
    background-position: 16px center;
    background-size: 40px;
    margin-bottom: 24px;

    h6 {
      font-size: 16px;
      line-height: 18px;
    }

    p {
      font-size: 14px;
      line-height: 16px;
    }
  }
}

@media screen and (max-width: 767px) {
  .homeMenuPage {
    background-color: $light-cart-bg;
  }
}

@media screen and (max-width: 480px) {
  .homeMenuButtons .pastOrdersMobile {
    background-image: none;
    padding: 16px 50px;

    h6 {
      font-size: 14px;
      line-height: 16px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/desktop/components/DesktopMenu.vue